import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosExt from '../axios';

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    //Typography
} from "@mui/material";
import { GridSortModel, GridFilterModel } from "@mui/x-data-grid";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

import LayoutAdmin from "../components/LayoutAdmin";
import AddIgnoreDialog from "../components/AddIgnoreDialog";
import { IgnoreItem } from "../types";
import { API_BASE_URL } from "../config.ts";


const IgnoreListPage: React.FC = () => {
    const { t } = useTranslation();

    const [items, setItems] = useState<IgnoreItem[]>([]);
    const [open, setOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<IgnoreItem | null>(null);

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<string | null>(null);

    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [filterModel, setFilterModel] = useState < GridFilterModel>({ items: [] });

    const savedSortKey = "sortIgnoreList1";
    const savedFilterKey = "filterIgnoreList1";

    useEffect(() => {
        const savedSortModel = JSON.parse(localStorage.getItem(savedSortKey) ?? "[]");
        const savedFilterModel = (() => {
            try {
                const parsed = JSON.parse(localStorage.getItem(savedFilterKey) ?? "{}");
                return parsed && parsed.items ? parsed : { items: [] };
            } catch (error) {
                console.error("Error parsing savedFilterKey:", error);
                return { items: [] }; // Fallback
            }
        })();
        setSortModel(savedSortModel);
        setFilterModel(savedFilterModel);

        fetchItems();
    }, []);

    // Save sortModel changes to localStorage
    const handleSortModelChange = (newModel: GridSortModel) => {
        setSortModel(newModel);
        localStorage.setItem(savedSortKey, JSON.stringify(newModel));
    };

    // Save filterModel changes to localStorage
    const handleFilterModelChange = (newModel: GridFilterModel) => {
        setFilterModel(newModel);
        localStorage.setItem(savedFilterKey, JSON.stringify(newModel));
    };

    const fetchItems = async () => {
        const token = localStorage.getItem("token");
        const response = await axiosExt.get(`${API_BASE_URL}/IgnoreList`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setItems(response.data);
    };

    const handleDelete = async (id: string) => {
        //const token = localStorage.getItem("token");

        try {
            setItemToDelete(id);
            setConfirmDeleteOpen(true);
            /*await axiosExt.delete(`${API_BASE_URL}/emails/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setEmails(emails.filter((c) => c.id !== id));*/
        } catch (error) {
            console.error("Error deleting email:", error);
        }
    };

    const handleConfirmDelete = async () => {
        if (itemToDelete === null) return;
        try {
            const token = localStorage.getItem("token");
            await axiosExt.delete(`${API_BASE_URL}/IgnoreList/${itemToDelete}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            fetchItems();
        } catch (error) {
            console.error("Error deleting:", error);
        } finally {
            setConfirmDeleteOpen(false);
            setItemToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setConfirmDeleteOpen(false);
        setItemToDelete(null);
    };


    const handleEdit = (item: IgnoreItem) => {
        setSelectedItem(item);
        //setType(item.type);
        //setValue(item.value);
        setOpen(true);
    };

    const columns: GridColDef[] = [
        { field: "type", headerName: t("Type"), width: 150 },
        { field: "value", headerName: t("Value"), width: 250 },
        {
            field: "actions",
            headerName: t("Actions"),
            width: 250,
            renderCell: (params) => (
                <>
                    <Button variant="contained" onClick={() => handleEdit(params.row)}>{t("Edit")}</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleDelete(params.row.id)}
                        style={{ marginLeft: 10 }}
                    >
                        {t("Delete")}
                    </Button>
                </>
            ),
        },
    ];

    return (
        <LayoutAdmin>
            <div style={{ height: "100%" }}>
                <div style={{ width: "100%", display: "flex" }}>
                    <h2>{t("Ignore List")}</h2>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setSelectedItem(null);  // Reset selectedItem to ensure POST request
                            setOpen(true);
                        }}
                        style={{ marginBottom: "20px", marginTop: "20px", marginLeft: "20px" }}
                    >
                        {t("Add")}
                    </Button>
                </div>
                <Box sx={{ width: "100%"}}>
                    <DataGrid rows={items} columns={columns}
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        filterModel={filterModel}
                        onFilterModelChange={handleFilterModelChange}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25, page: 0 },
                            },
                        }}
                        pageSizeOptions={[25, 50, 100, 200]} // Optional: Specify page size options
                    />
                </Box>

                <AddIgnoreDialog
                    open={open}
                    onClose={() => setOpen(false)}
                    onSave={async (type, value) => {
                        const token = localStorage.getItem("token");
                        const payload = { type, value };

                        const isEdit = !!selectedItem?.id;
                        const url = `${API_BASE_URL}/IgnoreList${isEdit ? `/${selectedItem.id}` : ""}`;
                        const method = isEdit ? axiosExt.put : axiosExt.post;

                        await method(url, payload, {
                            headers: { Authorization: `Bearer ${token}` },
                        });

                        setOpen(false);
                        fetchItems();
                    }}
                    initialType={selectedItem?.type}
                    initialValue={selectedItem?.value}
                />
                <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete}>
                    <DialogTitle>{t("Confirm Deletion")}</DialogTitle>
                    <DialogContent>{t("Are you sure you want to delete this record?")}</DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelDelete} color="secondary">{t("no")}</Button>
                        <Button onClick={handleConfirmDelete} color="error">{t("yes")}</Button>
                    </DialogActions>
                </Dialog>
            </div>
        </LayoutAdmin>
    );
};

export default IgnoreListPage;
